import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { MegaPanel } from './menuData';

interface MegaMenuPanelProps {
  panel: MegaPanel;
  onNavigate: () => void;
}

/**
 * Full-width dropdown panel: grouped link columns plus an optional intro
 * block, promo card and a closing action strip.
 */
const MegaMenuPanel: React.FC<MegaMenuPanelProps> = ({ panel, onNavigate }) => {
  const linkClass =
    'group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors duration-150 hover:bg-brand-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue';

  return (
    <div className="mega-panel bg-white text-brand-navy shadow-[0_24px_48px_-24px_rgba(0,31,95,0.35)]">
      {/* Matches the .container width/padding used by the nav bar above */}
      <div className="mx-auto w-full max-w-[1280px] px-4">
        <div className="flex gap-10 py-8">
          {/* Optional section intro */}
          {panel.intro && (
            <div className="w-[248px] flex-shrink-0">
              <h2 className="text-base font-bold text-brand-navy">{panel.intro.title}</h2>
              <p className="mt-2.5 text-sm leading-relaxed text-secondary-500">
                {panel.intro.text}
              </p>
              <Link
                to={panel.intro.ctaHref}
                onClick={onNavigate}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-navy px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
              >
                {panel.intro.ctaLabel}
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          )}

          {/* Link columns */}
          <div
            className={`grid flex-1 gap-x-8 gap-y-7 ${
              panel.columns.length >= 4
                ? 'grid-cols-4'
                : panel.columns.length === 3
                ? 'grid-cols-3'
                : 'grid-cols-2'
            }`}
          >
            {panel.columns.map((column, columnIndex) => (
              <div key={columnIndex} className="space-y-7">
                {column.map((group) => (
                  <div key={group.heading}>
                    <h3 className="px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-secondary-400">
                      {group.heading}
                    </h3>
                    <ul className="mt-2 space-y-0.5">
                      {group.links.map((link) => {
                        const Icon = link.icon;
                        return (
                          <li key={link.href + link.name}>
                            <Link to={link.href} onClick={onNavigate} className={linkClass}>
                              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand-blue transition-colors group-hover:bg-white">
                                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                              </span>
                              <span className="text-sm font-medium leading-snug text-brand-navy transition-colors group-hover:text-brand-blue">
                                {link.name}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Promo card */}
          {panel.promo && (
            <div className="w-[300px] flex-shrink-0">
              <div className="flex h-full flex-col rounded-2xl bg-gradient-to-br from-brand-navy to-[#003a9e] p-6 text-white">
                {panel.promo.eyebrow && (
                  <span className="inline-flex w-fit items-center rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                    {panel.promo.eyebrow}
                  </span>
                )}
                <h3 className="mt-4 text-xl font-bold leading-snug">{panel.promo.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/80">{panel.promo.text}</p>
                {panel.promo.bullets && (
                  <ul className="mt-4 space-y-2">
                    {panel.promo.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-center gap-2 text-sm text-white/90">
                        <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-[#7dd3fc]" aria-hidden="true" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-auto pt-6">
                  <Link
                    to={panel.promo.ctaHref}
                    onClick={onNavigate}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-brand-navy transition-colors hover:bg-brand-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy"
                  >
                    {panel.promo.ctaLabel}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Closing action strip */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-brand-line py-4">
          {/* The intro block already links to the section landing page */}
          {panel.href && !panel.intro ? (
            <Link
              to={panel.href}
              onClick={onNavigate}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy transition-colors hover:text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
            >
              View all {panel.label}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-sm text-secondary-500">{panel.footerNote}</p>
            <Link
              to="/contact-us"
              onClick={onNavigate}
              className="inline-flex h-10 items-center rounded-full border border-brand-line px-5 text-sm font-semibold text-brand-navy transition-colors hover:border-brand-blue/40 hover:bg-brand-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
            >
              Talk to us
            </Link>
            <Link
              to="/open-account"
              onClick={onNavigate}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-brand-navy px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
            >
              Open an account
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MegaMenuPanel;
